import { useElementDistribution } from '../../hooks/useElementDistribution';
import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SupportersList as DonorList } from 'donation-elements';
import { getAuthToken, getAuthGroups } from '../../utils/auth';
import { toast } from 'sonner';
import { useBrandColors } from '@charitystack/shared';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import '../../css/multi-select-styling.css';
import { Label } from '../../../components/ui/label';
import type {
  DonorListFormData,
  CreateDonorListDrawerProps,
  DonorData,
  Tab,
  WorkspaceHeaderAction,
} from './types/DonorListTypes';

// Import tab components
import DonorListConfiguration from './tabs/DonorListConfiguration';

import type { FormData } from './tabs/types';
import { copyToClipboard } from '@charitystack/shared';
import { portalClient } from '../../lib/trpc/portal';

// Utility function for classNames
function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

const tabs: Tab[] = [{ name: 'Configuration', step: 'configuration', current: true }];

const initialFormData: DonorListFormData = {
  elementName: '',
  connectedForms: [],
  donorListType: 'RECENT_DONORS',
  showAllSupportersAnonymous: false,
};

export default function CreateDonorListDrawer(props: CreateDonorListDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<DonorListFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<FormData[]>([]);
  const [topDonors, setTopDonors] = useState<DonorData[]>([]);
  const [recentDonors, setRecentDonors] = useState<DonorData[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const { canDistribute, refresh: verifyDistribution } = useElementDistribution(
    {
      ...props.elementData,
      elementID,
      active,
      elementType: 'DONOR_LIST',
      formIDs: [
        ...(props.elementData?.formIDs || []),
        ...formData.connectedForms
          .map((name) => availableForms.find((form) => form.formName === name)?.formID)
          .filter((id): id is string => !!id),
      ],
    },
    paymentsReady,
    props.open,
  );

  // Generate brand colors for preview, defaulting to the org's brand color
  // (falls back to a neutral green when the org has no brand color set).
  const { brandColors } = useBrandColors(props.merchant?.brandColor || '#66b089');

  // Add validation states
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');

  // Add loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Add form reference
  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Update formData helper
  const updateFormData = (field: keyof DonorListFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      // Remove the exact field name
      newInvalidFields.delete(fieldName);
      // Also try to remove non-prefixed version for backward compatibility
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid
  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      // Check for exact match first
      if (invalidFields.has(fieldName)) {
        return true;
      }
      // Check for prefixed versions
      for (const tab of tabs) {
        if (invalidFields.has(`${tab.step}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Generate embed code
  const generateEmbedCode = (): string => {
    if (!elementID) return '';
    return `<div data-entity="DONOR_LIST" data-source="CharityStack" id="${elementID}"></div>`;
  };

  // Copy embed code to clipboard
  const copyEmbedCode = async (): Promise<void> => {
    if (!canDistribute || !(await verifyDistribution())) return;
    const code = generateEmbedCode();
    if (code) {
      const success = await copyToClipboard(code);
      if (success) {
        toast.success('Embed code copied to clipboard');
      } else {
        toast.error('Failed to copy embed code');
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (!formData.connectedForms || formData.connectedForms.length === 0) {
      newInvalidFields.add('configuration-connectedForms');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      focusFirstInvalidElement(rightPanelRef.current);
      toast.error('Please fix validation errors');
      return;
    }

    setIsSaving(true);

    try {
      const payload: any = {
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        elementName: formData.elementName,
        elementType: 'DONOR_LIST',
        listType: formData.donorListType,
        formNames: formData.connectedForms,
        formIDs: formData.connectedForms
          .map((formName) => availableForms.find((form) => form.formName === formName)?.formID)
          .filter((id) => id !== undefined),
        showAllSupportersAnonymous: formData.showAllSupportersAnonymous ?? false,
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: props.user?.email || 'unknown',
        }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      toast.success(
        elementID
          ? 'Element updated successfully'
          : paymentsReady
            ? 'Element published successfully'
            : 'Draft saved successfully',
      );
      if (!elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
      }
      setRecentDonors(data.recentDonors);
      setTopDonors(data.topDonors);
      if (props.onSuccess) props.onSuccess(data);
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      };

      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`;
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;

      try {
        const merchants = await getAuthGroups();

        const response = await portalClient.postFormAndFundData({
          authorization: await getAuthToken(),
          body: {
            queryStringParameters: {
              nonprofit: merchants[0],
            },
          },
        });

        const data = await response.json();
        setAvailableForms(data.formNames);
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      }
    };

    fetchForms();
  }, [props.open]);

  // Tracks the connectedForms the preview is already in sync with, so the
  // refresh effect below fires ONLY on a deliberate user change — not on the
  // initial open/load. On edit-mode open we seed donors from elementData and
  // set connectedForms in the same commit; without this guard the preview
  // effect would run first seeing the stale empty list, clear those donors,
  // then refetch 400ms later (donors flash away and reappear). Seeded by the
  // edit-load and reset by clearStates.
  const lastSyncedConnectedFormsRef = useRef<string[] | null>(null);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;

      setElementID(data.elementID);
      setRecentDonors(data?.recentDonors);
      setTopDonors(data?.topDonors);
      const loadedForms = data.formNames || [];
      // Mark the loaded forms as already-synced: elementData carries the
      // donors the backend computed for exactly this list, so the preview
      // effect must not clear/refetch them on open.
      lastSyncedConnectedFormsRef.current = loadedForms;
      setFormData({
        elementName: data.elementName,
        connectedForms: loadedForms,
        donorListType: data.listType || 'RECENT_DONORS',
        showAllSupportersAnonymous: data.showAllSupportersAnonymous ?? false,
      });
      setActive(data.active);
    }
  }, [props.elementData, props.open]);

  // Refresh the live supporter preview in place whenever the connected-form
  // list changes, so the preview reflects the current selection BEFORE the
  // user saves. Uses the read-only donor-list preview endpoint (computes
  // donors for the selected forms without persisting). Debounced so rapid
  // multi-select edits collapse into a single request, and aborted on the
  // next change / unmount to avoid out-of-order responses overwriting newer
  // data.
  useEffect(() => {
    if (!props.open) return undefined;

    const merchantName = props.merchant?.merchantName;
    if (!merchantName) return undefined;

    const forms = formData.connectedForms || [];

    // Skip when the forms haven't actually changed since the preview was last
    // synced — in particular the initial open/edit-load commit, where donors
    // are already populated from elementData. Only a deliberate user edit to
    // the connected-form list should drive a clear/refetch.
    const lastSynced = lastSyncedConnectedFormsRef.current;
    if (
      lastSynced !== null &&
      lastSynced.length === forms.length &&
      lastSynced.every((name, i) => name === forms[i])
    ) {
      return undefined;
    }
    lastSyncedConnectedFormsRef.current = forms;

    // No forms selected → clear the preview immediately (nothing to fetch).
    if (forms.length === 0) {
      setRecentDonors([]);
      setTopDonors([]);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          type: 'donor_list_preview',
          nonprofit: merchantName,
          formNames: forms.join(','),
        });
        const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements?${params.toString()}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthToken(),
          },
          signal: controller.signal,
        });

        if (!response.ok) return;

        const data = await response.json();
        setRecentDonors(data.recentDonors || []);
        setTopDonors(data.topDonors || []);
      } catch (error: any) {
        // Ignore aborts (superseded by a newer change); log real failures.
        if (error?.name !== 'AbortError') {
          console.error('Error refreshing donor list preview:', error);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
    // formData.connectedForms is the trigger; merchantName/open gate the fetch.
  }, [formData.connectedForms, props.merchant?.merchantName, props.open]);

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setRecentDonors([]);
    setTopDonors([]);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);
    // Reset so the next open (edit or create) re-seeds the preview-sync guard
    // cleanly rather than comparing against the previous element's forms.
    lastSyncedConnectedFormsRef.current = null;

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  // Don't render if required props are missing
  if (!props.merchant) {
    return null;
  }

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header with Action Buttons */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
                      fill="#6B6458"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {elementID ? 'Edit Donor List' : 'Create Donor List'}
                  </p>
                  <p className="text-sm text-ink-muted">Display a list of recent supporters</p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {/* Activate/Deactivate Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {/* Publish Button - Only when creating */}
                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto'
            }`}
          >
            {/* Left Side - Preview Section */}
            <div
              className={`flex flex-col gap-4 pr-2 lg:w-1/2 ${
                elementID
                  ? 'min-h-[800px] xs:min-h-[700px] sm:min-h-[600px] lg:max-h-none'
                  : 'h-[120px]'
              }`}
            >
              {/* CON-42: this frame is dashboard chrome; its contents are a donor
                  component, which would otherwise resolve every --cs-* against
                  dashboard.css. See decisions.md D-previewer-scope. */}
              <div
                data-cs-position="donor"
                className="flex w-full flex-col items-stretch rounded-md border border-edge bg-paper-muted px-4 py-4"
              >
                <DonorList
                  donorListID={elementID}
                  isStandAlone={true}
                  donorListType={formData.donorListType}
                  connectedForms={formData.connectedForms}
                  donorsList={
                    formData.donorListType === 'RECENT_DONORS'
                      ? recentDonors.slice(0, 4)
                      : topDonors.slice(0, 4)
                  }
                  brandColors={brandColors}
                  showAllSupportersAnonymous={formData.showAllSupportersAnonymous}
                />
              </div>

              {elementID && canDistribute && (
                <div className="mt-4 lg:mb-6">
                  <Label className="mb-2 text-sm font-medium text-ink">Embed Code</Label>
                  <div className="relative w-full">
                    <textarea
                      value={generateEmbedCode()}
                      rows={1}
                      className="block h-14 w-full resize-none rounded-md border border-edge-input bg-paper-inset pr-20 text-xs shadow-sm"
                      readOnly
                      onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-3 flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
                      onClick={copyEmbedCode}
                      title="Copy Embed Code"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'min-h-[450px] overflow-hidden pb-14 lg:sticky lg:top-0 lg:h-[calc(100vh-160px)] lg:pb-0'
              }`}
            >
              {/* Form Tabs */}
              {!useCreateWorkspaceCards && (
                <div className="border-edge bg-white">
                  <nav
                    className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 lg:overflow-x-hidden lg:pb-0"
                    aria-label="Tabs"
                  >
                    {tabs.map((tab) => {
                      // Check if this tab has errors by looking for fields with the tab's step prefix
                      const tabHasErrors = Array.from(invalidFields).some((field) =>
                        field.startsWith(tab.step + '-'),
                      );
                      return (
                        <button
                          type="button"
                          key={tab.name}
                          onClick={() => setCurrentStep(tab.step)}
                          className={classNames(
                            currentStep === tab.step
                              ? tabHasErrors
                                ? 'border-red-500 text-red-600'
                                : 'border-ink-secondary text-ink-secondary'
                              : tabHasErrors
                                ? 'border-transparent text-red-500 hover:text-red-600'
                                : 'border-transparent text-ink-muted hover:text-ink-secondary',
                            'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                          )}
                        >
                          {tab.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Form Content */}
              <div
                className={`flex-1 ${
                  useCreateWorkspaceCards ? 'overflow-visible' : 'overflow-y-auto py-4'
                }`}
              >
                {/* Configuration Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-2'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Choose the connected forms and which supporters to display."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    <DonorListConfiguration
                      formData={formData}
                      updateFormData={updateFormData}
                      availableForms={availableForms}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                    />
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
